import { axiosGetV3 } from '../../api/axios-handler';
import { useQuery } from '@tanstack/react-query';
import { getSessionSeed } from '../../utils/sessionSeed';
import { People } from './useExperiences';
import { useCrisisCheckOnSearchQuery } from './useCrisisCheck';

export interface PeersResponse {
  count: number;
  data: People[];
}

// Mixed/peersona/"All" selections are left unmapped so the backend returns both
// peers and listeners (e.g. on global search).
const PEER_TYPE_BY_LISTENER_TYPE: Record<string, 'listener' | 'peer'> = {
  peer_listener: 'listener',
  peer: 'peer',
};

const derivePeerTypeFilter = (listenerTypes?: string[]): 'listener' | 'peer' | undefined => {
  if (listenerTypes?.length !== 1) return undefined;
  return PEER_TYPE_BY_LISTENER_TYPE[listenerTypes[0]];
};

const getPeers = async (
  tagGroupId?: number,
  tagIds?: number[],
  listenerTypes?: string[],
  searchQuery?: string,
  available?: boolean,
  page?: number,
  limit?: number,
): Promise<PeersResponse> => {
  const params: any = {};

  if (searchQuery && searchQuery.trim().length > 0) {
    params.q = searchQuery.trim();
    params.source_type = 'user';
    params.seed = getSessionSeed();
    if (tagGroupId) {
      params.tag_groups = tagGroupId.toString();
    }
    if (tagIds && tagIds.length > 0) {
      params.tag_ids = tagIds.join(',');
    }
    if (listenerTypes && listenerTypes.length > 0) {
      params.listener_type = listenerTypes.join(',');
    }
    const peerType = derivePeerTypeFilter(listenerTypes);
    if (peerType) {
      params.peer_type = peerType;
    }
    const response = await axiosGetV3('/simple_search/algolia/search', params);
    return response.data;
  }

  if (tagGroupId) {
    params.tag_groups = tagGroupId.toString();
  }
  if (tagIds && tagIds.length > 0) {
    params.tag_ids = tagIds.join(',');
  }
  if (listenerTypes && listenerTypes.length > 0) {
    params.listener_type = listenerTypes.join(',');
  }
  if (available !== undefined) {
    params.available = available.toString();
  }
  if (page !== undefined) {
    params.page = page.toString();
  }
  if (limit !== undefined) {
    params.limit = limit.toString();
  }
  params.seed = getSessionSeed();
  const response = await axiosGetV3('/peers/browse', params);
  return response.data;
};

export interface UsePeersByTopicOptions {
  enabled?: boolean;
}

export const usePeersByTopic = (
  tagGroupId?: number,
  tagIds?: number[],
  listenerTypes?: string[],
  searchQuery?: string,
  available?: boolean,
  page?: number,
  limit?: number,
  options?: UsePeersByTopicOptions,
) => {
  useCrisisCheckOnSearchQuery(searchQuery);

  const tagIdsKey = tagIds && tagIds.length > 0 ? [...tagIds].sort((a, b) => a - b).join(',') : undefined;
  const listenerTypesKey = listenerTypes && listenerTypes.length > 0 ? [...listenerTypes].sort().join(',') : undefined;
  const enabled = options?.enabled !== false;

  return useQuery<PeersResponse>(
    ['peersByTopic', tagGroupId, tagIdsKey, listenerTypesKey, searchQuery, available, page, limit],
    () => getPeers(tagGroupId, tagIds, listenerTypes, searchQuery, available, page, limit),
    { enabled },
  );
};
